import React, { useState, useEffect } from 'react';
import { withdrawalAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Banknote, Check, Upload, Clock, CheckCircle2 } from 'lucide-react';

const AdminWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [slipFile, setSlipFile] = useState(null);

  useEffect(() => {
    loadWithdrawals();
  }, []);

  const loadWithdrawals = async () => {
    try {
      const res = await withdrawalAPI.getWithdrawals();
      setWithdrawals(res.data);
    } catch (error) {
      console.error('Error loading withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await withdrawalAPI.approve(id);
      toast.success('Withdrawal approved');
      loadWithdrawals();
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    }
  };

  const handleUploadSlip = async () => {
    if (!slipFile || !selectedWithdrawal) return;
    try {
      await withdrawalAPI.uploadSlip(selectedWithdrawal.id, slipFile);
      toast.success('Payment slip uploaded and withdrawal completed');
      setSelectedWithdrawal(null);
      setSlipFile(null);
      loadWithdrawals();
    } catch (error) {
      toast.error('Failed to upload payment slip');
    }
  };

  const filteredWithdrawals = withdrawals.filter((w) => {
    if (activeTab === 'all') return true;
    return w.status === activeTab;
  });

  const totalPending = withdrawals.filter(w => w.status === 'pending').reduce((sum, w) => sum + w.amount, 0);

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700 border-green-200',
      approved: 'bg-blue-100 text-blue-700 border-blue-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <Badge variant="outline" className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-withdrawals">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Banknote className="h-6 w-6 text-[#4E50FF]" />
          Withdrawal Requests
        </h1>
        <p className="text-slate-500 mt-1">Process user withdrawal requests</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600 font-mono">{formatCurrency(totalPending)}</p>
            <p className="text-sm text-amber-700">Pending Amount</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <p className="text-3xl font-bold text-slate-900">{withdrawals.filter(w => w.status === 'pending').length}</p>
            </div>
            <p className="text-sm text-slate-500">Pending Requests</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <p className="text-3xl font-bold text-green-600">{withdrawals.filter(w => w.status === 'completed').length}</p>
            </div>
            <p className="text-sm text-slate-500">Completed</p>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-900">{withdrawals.length}</p>
            <p className="text-sm text-slate-500">Total Requests</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredWithdrawals.length > 0 ? (
            <Card className="border shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600">Amount</TableHead>
                    <TableHead className="text-slate-600">Bank Details</TableHead>
                    <TableHead className="text-slate-600">Status</TableHead>
                    <TableHead className="text-slate-600">Date</TableHead>
                    <TableHead className="text-slate-600">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWithdrawals.map((withdrawal) => (
                    <TableRow key={withdrawal.id} data-testid={`withdrawal-row-${withdrawal.id}`}>
                      <TableCell className="text-green-600 font-mono font-semibold">
                        {formatCurrency(withdrawal.amount)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-slate-900 font-medium">{withdrawal.bank_name}</p>
                          <p className="text-sm text-slate-500 font-mono">{withdrawal.bank_account}</p>
                          <p className="text-sm text-slate-400">{withdrawal.bank_holder_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(withdrawal.status)}</TableCell>
                      <TableCell className="text-slate-500">{formatDate(withdrawal.created_at)}</TableCell>
                      <TableCell>
                        {withdrawal.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleApprove(withdrawal.id)}
                            className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                            data-testid={`approve-withdrawal-${withdrawal.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        {withdrawal.status === 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => setSelectedWithdrawal(withdrawal)}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid={`upload-slip-${withdrawal.id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Upload Slip
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card className="text-center py-12 border shadow-sm">
              <CardContent>
                <div className="w-16 h-16 bg-[#4E50FF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Banknote className="w-8 h-8 text-[#4E50FF]" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Withdrawals</h3>
                <p className="text-slate-500">
                  {activeTab === 'all' ? 'No withdrawal requests yet' : `No ${activeTab} withdrawals`}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Payment Slip Dialog */}
      <Dialog open={!!selectedWithdrawal} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#4E50FF]" />
              Upload Payment Slip
            </DialogTitle>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-mono font-semibold text-green-600">
                    {formatCurrency(selectedWithdrawal.amount)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Bank</span>
                  <span className="text-slate-900">{selectedWithdrawal.bank_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Account</span>
                  <span className="font-mono text-slate-900">{selectedWithdrawal.bank_account}</span>
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-600 block mb-2">Payment Slip</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSlipFile(e.target.files[0])}
                />
              </div>

              <Button
                onClick={handleUploadSlip}
                disabled={!slipFile}
                className="w-full bg-green-500 hover:bg-green-600 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload & Complete
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWithdrawals;
